import { useState, useEffect } from "react";

import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import { LoadingButton } from "@aclymatepackages/atoms";
import { emailRegExpTest } from "@aclymatepackages/reg-exp";

import GrayPaper from "../../../../atoms/GrayPaper";

import { useAccountData, useAuth } from "../../../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../../../helpers/components/vendors";
import usePermissions from "../../../../../helpers/hooks/usePermissions";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";

const emptyForm = { name: "", email: "", title: "", notes: "" };

const isFormValid = ({ name, email }) => {
  const hasNameOrEmail = !!(name || email);
  const isEmailValid = !email || emailRegExpTest(email);
  return hasNameOrEmail && isEmailValid;
};

const ContactForm = ({ initial = emptyForm, onSave, onCancel, isSaving }) => {
  const [form, setForm] = useState(initial);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <TextField
          label="Name"
          size="small"
          fullWidth
          value={form.name}
          onChange={setField("name")}
        />
      </Grid>
      <Grid item>
        <TextField
          label="Email"
          size="small"
          fullWidth
          value={form.email}
          onChange={setField("email")}
          error={!!form.email && !emailRegExpTest(form.email)}
        />
      </Grid>
      <Grid item>
        <TextField
          label="Title"
          size="small"
          fullWidth
          value={form.title}
          onChange={setField("title")}
        />
      </Grid>
      <Grid item>
        <TextField
          label="Notes"
          size="small"
          fullWidth
          value={form.notes}
          onChange={setField("notes")}
        />
      </Grid>
      <Grid item>
        <Box display="flex" gap={1}>
          <Tooltip title="Save">
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={isSaving || !isFormValid(form)}
                onClick={() => onSave(form)}
              >
                {isSaving ? <CircularProgress size={16} /> : <CheckIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={onCancel}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};

const ContactInviteAction = ({
  contact,
  onInvite,
  isInviting,
  onAddAdminAsContact,
  canEdit
}) => {
  const { accessStatus, accessAdminName, accessAdminEmail, explorerInvitedAt } =
    contact;

  if (accessStatus === "active") {
    return (
      <Chip
        size="small"
        label="Has Account"
        color="success"
        variant="outlined"
      />
    );
  }

  if (accessStatus === "pending-approval") {
    return (
      <Chip
        size="small"
        label="Pending Admin Approval"
        color="warning"
        variant="outlined"
      />
    );
  }

  if (accessStatus === "declined") {
    return (
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Chip
            size="small"
            label="Access Declined"
            color="error"
            variant="outlined"
          />
        </Grid>
        {accessAdminName && (
          <Grid item>
            <GrayPaper>
              <Box p={1}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Reach out to their admin:
                </Typography>
                <Typography variant="body2">{accessAdminName}</Typography>
                {accessAdminEmail && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    {accessAdminEmail}
                  </Typography>
                )}
                {onAddAdminAsContact && (
                  <Box mt={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      disabled={!canEdit}
                      onClick={() =>
                        onAddAdminAsContact({
                          name: accessAdminName,
                          email: accessAdminEmail || ""
                        })
                      }
                    >
                      Add as Contact
                    </Button>
                  </Box>
                )}
              </Box>
            </GrayPaper>
          </Grid>
        )}
      </Grid>
    );
  }

  if (explorerInvitedAt) {
    return (
      <Chip size="small" label="Invited" color="warning" variant="outlined" />
    );
  }

  return (
    <LoadingButton
      isLoading={isInviting}
      onClick={onInvite}
      variant="outlined"
      color="primary"
      size="small"
      label="Invite to Explorer"
      disabled={!contact.email || !canEdit}
    />
  );
};

const ContactRow = ({
  contact,
  onEdit,
  onDelete,
  onInvite,
  isInviting,
  onAddAdminAsContact,
  canEdit
}) => {
  const { name, email, title, notes } = contact;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2">
          {name || email || "Unnamed contact"}
        </Typography>
        {name && email && (
          <Typography variant="caption" color="textSecondary" display="block">
            {email}
          </Typography>
        )}
        {title && (
          <Typography variant="caption" color="textSecondary" display="block">
            {title}
          </Typography>
        )}
        {notes && (
          <Typography variant="caption" color="textSecondary" display="block">
            {notes}
          </Typography>
        )}
      </Grid>
      <Grid item>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <ContactInviteAction
            contact={contact}
            onInvite={onInvite}
            isInviting={isInviting}
            onAddAdminAsContact={onAddAdminAsContact}
            canEdit={canEdit}
          />
          <Tooltip title="Edit">
            <span>
              <IconButton size="small" onClick={onEdit} disabled={!canEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton size="small" onClick={onDelete} disabled={!canEdit}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};

const ContactsTab = ({
  vendorId,
  relationshipId,
  vendorContacts = [],
  contactsField = "vendorContacts",
  inviteApiPath = "/vendors/new-vendor-survey-invite",
  getExtraInviteData = () => ({
    buyerId: window.sessionStorage.getItem("accountId")
  }),
  individualsMap = {},
  onIndividualResolved = () => {}
}) => {
  const [{ name: companyName }] = useAccountData();
  const [user] = useAuth();
  const { displayName: userName, email: userEmail } = user || {};
  // contactsField tells us which side of the relationship this instance
  // edits ("vendorContacts" from the buyer's Vendors page, "customerContacts"
  // from the vendor's Customers page) -- that's the same alias its cached
  // list is registered under, so it doubles as the SWR revalidation key.
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields(
    contactsField === "customerContacts" ? "customers" : "vendor-relationships"
  );
  const { canEdit } = usePermissions();

  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [invitingId, setInvitingId] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [joinRequestContact, setJoinRequestContact] = useState(null);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const [localIndividualsMap, setLocalIndividualsMap] =
    useState(individualsMap);

  useEffect(() => {
    setLocalIndividualsMap(individualsMap);
  }, [individualsMap]);

  const displayContacts = vendorContacts.map((contact) => ({
    ...contact,
    ...(contact.individualId && localIndividualsMap[contact.individualId]
      ? {
          name: localIndividualsMap[contact.individualId].name,
          email: localIndividualsMap[contact.individualId].email
        }
      : {})
  }));

  const saveContacts = (updated) =>
    updateVendorRelationshipFields(relationshipId, {
      [contactsField]: updated
    });

  const resolveIndividualId = async (email, name) => {
    if (!email) {
      return null;
    }
    const result = await fetchOurApi({
      path: "/vendors/find-or-create-contact-individual",
      method: "POST",
      data: {
        email,
        name,
        vendorId,
        buyerId: window.sessionStorage.getItem("accountId")
      },
      callback: (res) => res
    });
    return result?.individualId || null;
  };

  const mergeIndividual = (individualId, { name, email }) => {
    setLocalIndividualsMap((prev) => ({
      ...prev,
      [individualId]: { id: individualId, name, email }
    }));
    onIndividualResolved(individualId, { name, email });
  };

  const onAddContact = async (form) => {
    setSavingId("new");
    const individualId = await resolveIndividualId(form.email, form.name);
    if (individualId) {
      mergeIndividual(individualId, { name: form.name, email: form.email });
    }
    const newContact = {
      id: crypto.randomUUID(),
      title: form.title || null,
      notes: form.notes || null,
      explorerInvitedAt: null,
      ...(individualId ? { individualId } : { name: form.name || null })
    };
    await saveContacts([...vendorContacts, newContact]);
    setIsAdding(false);
    setSavingId(null);
  };

  const onEditContact = async (id, form) => {
    setSavingId(id);
    const individualId = await resolveIndividualId(form.email, form.name);
    if (individualId) {
      mergeIndividual(individualId, { name: form.name, email: form.email });
    }
    const updated = vendorContacts.map((c) =>
      c.id === id
        ? {
            ...c,
            name: form.name || null,
            email: form.email || null,
            title: form.title || null,
            notes: form.notes || null,
            ...(individualId ? { individualId } : {})
          }
        : c
    );
    await saveContacts(updated);
    setEditingId(null);
    setSavingId(null);
  };

  const onDeleteContact = (id) =>
    saveContacts(vendorContacts.filter((c) => c.id !== id));

  const onInviteContact = async (contact) => {
    setInvitingId(contact.id);
    setInviteError("");
    try {
      const { eligible, reason, requiresJoinRequest } = await fetchOurApi({
        path: "/vendors/check-invite-eligibility",
        method: "POST",
        data: { email: contact.email, vendorId },
        callback: (res) => res
      });

      if (requiresJoinRequest) {
        setJoinRequestContact(contact);
        return;
      }

      if (!eligible) {
        const errorMessages = {
          "already-on-vendor":
            "This contact already has an account for this vendor.",
          "existing-account": `${contact.email} is already tied to a different Aclymate account.`
        };
        setInviteError(
          errorMessages[reason] || "This contact cannot be invited."
        );
        return;
      }

      await fetchOurApi({
        path: inviteApiPath,
        method: "POST",
        data: {
          vendorId,
          relationshipId,
          ...getExtraInviteData(),
          recipientName: contact.name,
          email: contact.email,
          companyName,
          senderName: userName
        },
        callback: () => {
          const updated = vendorContacts.map((c) =>
            c.id === contact.id ? { ...c, explorerInvitedAt: new Date() } : c
          );
          saveContacts(updated);
        }
      });
    } finally {
      setInvitingId(null);
    }
  };

  const onConfirmJoinRequest = async () => {
    if (!joinRequestContact) {
      return;
    }
    setIsRequestingAccess(true);
    try {
      await fetchOurApi({
        path: "/vendors/request-vendor-account-access",
        method: "POST",
        data: {
          vendorId,
          buyerId: window.sessionStorage.getItem("accountId"),
          relationshipId,
          contactEmail: joinRequestContact.email,
          contactName: joinRequestContact.name || "",
          contactId: joinRequestContact.id,
          buyerContactEmail: userEmail || "",
          buyerContactName: userName || ""
        },
        callback: () => {
          const updated = vendorContacts.map((c) =>
            c.id === joinRequestContact.id
              ? { ...c, accessStatus: "pending-approval" }
              : c
          );
          saveContacts(updated);
          setJoinRequestContact(null);
        }
      });
    } finally {
      setIsRequestingAccess(false);
    }
  };

  return (
    <Box p={2}>
      <Grid container direction="column" spacing={2}>
        {displayContacts.map((contact) => (
          <Grid item key={contact.id}>
            <GrayPaper>
              <Box p={2}>
                {editingId === contact.id ? (
                  <ContactForm
                    initial={{
                      name: contact.name || "",
                      email: contact.email || "",
                      title: contact.title || "",
                      notes: contact.notes || ""
                    }}
                    onSave={(form) => onEditContact(contact.id, form)}
                    onCancel={() => setEditingId(null)}
                    isSaving={savingId === contact.id}
                  />
                ) : (
                  <ContactRow
                    contact={contact}
                    onEdit={() => setEditingId(contact.id)}
                    onDelete={() => onDeleteContact(contact.id)}
                    onInvite={() => onInviteContact(contact)}
                    isInviting={invitingId === contact.id}
                    canEdit={canEdit}
                    onAddAdminAsContact={
                      canEdit &&
                      contact.accessAdminEmail &&
                      !displayContacts.some(
                        (c) =>
                          c.id !== contact.id &&
                          c.email === contact.accessAdminEmail
                      )
                        ? onAddContact
                        : null
                    }
                  />
                )}
              </Box>
            </GrayPaper>
          </Grid>
        ))}
        {isAdding && (
          <Grid item>
            <GrayPaper>
              <Box p={2}>
                <ContactForm
                  onSave={onAddContact}
                  onCancel={() => setIsAdding(false)}
                  isSaving={savingId === "new"}
                />
              </Box>
            </GrayPaper>
          </Grid>
        )}
        {inviteError && (
          <Grid item>
            <Typography variant="caption" color="error">
              {inviteError}
            </Typography>
          </Grid>
        )}
        {!isAdding && (
          <Grid item>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PersonAddIcon />}
                disabled={!canEdit}
                onClick={() => setIsAdding(true)}
              >
                Add Contact
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      <Dialog
        open={!!joinRequestContact}
        onClose={() => setJoinRequestContact(null)}
      >
        <DialogTitle>Request Access for This Contact</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This vendor already has an Aclymate account. We&apos;ll notify their
            admin to grant{" "}
            <strong>
              {joinRequestContact?.name || joinRequestContact?.email}
            </strong>{" "}
            access. You&apos;ll see the status update on this contact once
            they&apos;ve responded.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinRequestContact(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onConfirmJoinRequest}
            disabled={isRequestingAccess}
          >
            {isRequestingAccess ? (
              <CircularProgress size={20} />
            ) : (
              "Send Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ContactsTab;
